import { ExclamationCircleIcon, SelectorIcon } from "@heroicons/react/outline";
import { useSelect } from "downshift";
import { FieldInputProps, FormikProps } from "formik";
import React, {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import classNames from "../../utils/classNames";
import usePrevious from "../../utils/usePrevious";
import CheckIcon from "../icons/CheckIcon";
import { useForm } from "./FormProvider";

interface Props<Item> {
  className?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  label?: string;
  field: FieldInputProps<any>;
  form: FormikProps<any>;
  placeholder?: string;
  helperText?: string;
  onSelectedItem?: (
    item: Item | null | undefined,
    clearInputCallback: () => void
  ) => void;
  getImageUrl?: (item: Item) => string;
  items: Item[];
  itemToString: (item: Item | null) => string;
  optionToString: (item: Item | null) => any;
  enableScroll?: boolean;
  withoutErrorLabel?: boolean;
}

function DropdownSelect<Item>({
  className,
  disabled,
  label,
  field,
  form: { touched, errors, submitCount, setFieldValue },
  placeholder = "Select item",
  helperText,
  onSelectedItem,
  getImageUrl,
  items,
  itemToString,
  optionToString,
  enableScroll = true,
  withoutErrorLabel,
  ...otherProps
}: PropsWithChildren<Props<Item>>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isFirstErrorField } = useForm();
  const prevSubmitCount = usePrevious(submitCount);

  const firstErrorField = isFirstErrorField(errors, field.name);
  const error = useMemo(
    () => (touched[field.name] || firstErrorField) && errors[field.name],
    [errors, field.name, firstErrorField, touched]
  );
  const hasError = useMemo(() => !!error, [error]);

  const scrollToElement = () => {
    if (containerRef.current?.scrollIntoView) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  };

  useEffect(() => {
    if (submitCount > prevSubmitCount && firstErrorField && enableScroll) {
      scrollToElement();
    }
  }, [submitCount, prevSubmitCount, firstErrorField, enableScroll]);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    reset
  } = useSelect({
    items,
    initialSelectedItem: field.value,
    onSelectedItemChange: ({ selectedItem }) => {
      if (onSelectedItem) {
        onSelectedItem(selectedItem, () => {
          setFieldValue(field.name, undefined);
          reset();
        });
      }

      return setFieldValue(field.name, selectedItem);
    },
    itemToString
  });

  const renderItemIcon = useCallback(
    (item: Item) => {
      let imageUrl = getImageUrl && getImageUrl(item);

      if (!imageUrl) return null;

      return (
        <img
          className="flex-shrink-0 h-5 w-5 rounded-full"
          src={imageUrl}
          alt={itemToString(item)}
        />
      );
    },
    [getImageUrl, itemToString]
  );

  return (
    <div
      className={classNames("mt-2 sm:gap-4 sm:items-start", className)}
      ref={containerRef}
      {...otherProps}
    >
      {label && (
        <label
          className="block text-sm font-medium text-gray-900 dark:text-white"
          {...getLabelProps()}
        >
          {label}
        </label>
      )}

      <div
        className={classNames(
          "mt-1 relative",
          (disabled === undefined ? false : disabled) &&
            "pointer-events-none opacity-50"
        )}
      >
        <span className="inline-block w-full rounded-md shadow-sm">
          <button
            type="button"
            className={classNames(
              "relative w-full rounded-md border pl-3 pr-10 py-2 text-left focus:outline-none focus:shadow-outline-purple focus:border-hotspotty-300 transition ease-in-out duration-150 text-xs sm:text-sm sm:leading-5 border-gray-300 bg-white dark:bg-gray-800 disabled:bg-gray-100",
              hasError ? "border-red-300 text-red-300" : "border-gray-300"
            )}
            {...getToggleButtonProps({
              id: `${field.name}-toggle`
            })}
            disabled={disabled === undefined ? false : disabled}
          >
            {field.value ? (
              <div className="flex items-center space-x-3">
                {field.value && renderItemIcon(field.value)}
                <span className="block truncate dark:text-white">
                  {field.value ? itemToString(field.value) : placeholder}
                </span>
              </div>
            ) : (
              <div className={classNames(!hasError && "text-gray-400")}>
                {placeholder}
              </div>
            )}

            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              {hasError ? (
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-500"
                  aria-hidden="true"
                />
              ) : (
                <SelectorIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              )}
            </span>
          </button>
        </span>

        <div
          className={classNames(
            "absolute mt-1 w-48 rounded-md bg-white shadow-lg z-10 dark:bg-gray-800",
            !(isOpen && items.length > 0) && "invisible"
          )}
        >
          <ul
            className="rounded-md py-1 text-xs leading-6 shadow-sm overflow-auto focus:outline-none sm:text-sm sm:leading-5"
            {...getMenuProps()}
          >
            {isOpen &&
              items.map((item, index) => (
                <Fragment key={`${itemToString(item)}${index}`}>
                  {optionToString(item) && (
                    <li
                      className={classNames(
                        "cursor-pointer select-none relative py-2 px-3",
                        highlightedIndex === index
                          ? "text-white bg-hotspotty-400 dark:bg-gray-700 dark:text-white"
                          : "text-gray-900 dark:bg-gray-800 dark:text-white"
                      )}
                      {...getItemProps({
                        item,
                        index
                      })}
                    >
                      <div className="flex items-center space-x-3">
                        {renderItemIcon(item)}
                        <span
                          className={classNames(
                            "block",
                            item === field.value
                              ? "font-semibold"
                              : "font-normal"
                          )}
                        >
                          {optionToString(item)}
                        </span>
                      </div>

                      {item === field.value && (
                        <span className="absolute top-2 right-0 flex items-center pr-2">
                          <CheckIcon className="h-5 w-5" />
                        </span>
                      )}
                    </li>
                  )}
                </Fragment>
              ))}
          </ul>
        </div>
      </div>

      {!withoutErrorLabel && (helperText || hasError) && (
        <p
          className={classNames(
            "field-error mt-2 text-sm",
            hasError ? "text-red-600" : "text-gray-600 dark:text-gray-300"
          )}
        >
          {hasError ? error : helperText}
        </p>
      )}
    </div>
  );
}

export default DropdownSelect;

//
// Utils
//

export interface DropdownSelectOption<Key> {
  key: Key;
  title: string;
}
